import {
  Box,
  Button,
  Card,
  CardHeader,
  Divider,
  Grid,
  Paper,
  SxProps,
  TextField,
  Theme,
  Typography
} from "@mui/material";
import React, { useEffect, useState, ReactNode } from "react";
import WSBackButton from "../../Components/BackButton/BackButton";
import {
  getComponentProps,
  unRegisterComponent,
  updateOrRegisterComponent
} from "../../StateManagement/RegisterComponent";
import { WSHeaders } from "../../commonFunctions/HeaderConfig/HeaderConfig";
import WSButton from "../Button/WSButton";

interface IHeaderProps {
  title: string;
  type?: "simple" | "card" | "light" | "dashboard" | "new";
  children?: React.ReactNode;
  isBackButton?: boolean;
  url?: string;
  id?: WSHeaders | any;
  isDivider?: boolean;
  headerSx?: SxProps<Theme>;
  search?: boolean;
  action?: IHeaderActions;
  icon?: ReactNode;
}

export interface IHeaderActions {
  type: "button";
  text: string;
  onClick?: (data?: any) => void;
  icon?: any;
  sx?: SxProps<Theme>;
  variant?: "text" | "outlined" | "contained";
  size?: "small" | "medium" | "large";
  color?: "primary" | "secondary" | "success" | "error" | "info" | "warning";
  disabled?: boolean;
  loading?: boolean;
}

export const Header: React.FC<IHeaderProps> = React.memo((props) => {
  const [newUrl, setUrl] = useState<string | undefined>(props?.url);
  const [hasBackButton, setBackButton] = useState<boolean | undefined>(
    props?.isBackButton
  );

  // Register the component with initial props
  useEffect(() => {
    updateOrRegisterComponent(props?.id, props);
  }, []);
  useEffect(
    () => () => {
      unRegisterComponent(props?.id);
    },
    []
  );
  // Handle updates from other components
  useEffect(() => {
    const propsed = getComponentProps(props?.id); // Retrieve the updated props
    if (propsed) {
      setUrl(propsed.url);
      setBackButton(propsed.isBackButton);
    }
  }, [props?.id]);

  return (
    <>
      {props?.type === "dashboard" && (
        <Grid
          container
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 3, ...props.headerSx }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: "600",
              fontSize: "32px",
              fontFamily: "Inter, sans-serif"
            }}
          >
            {props.title}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {props?.search && (
              <TextField
                size="small"
                placeholder="Search"
                sx={{
                  backgroundColor: "#c9c9c9",
                  borderRadius: 1,
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: "none"
                  }
                }}
              />
            )}

            {props?.action && (
              <Grid sx={{ display: "flex", gap: 2 }}>
                {props.action && (
                  <WSButton
                    key={props.action.text}
                    id={props.action.text}
                    label={props.action.text}
                    type={props.action?.type || "button"}
                    size={props.action?.size || "medium"}
                    sx={props.action?.sx}
                    onClick={props.action?.onClick}
                    className="ws-theme-crm"
                    color={props.action?.color}
                    variant={props.action?.variant || "contained"}
                    disabled={props.action.disabled}
                    loading={props.action?.loading}
                  />
                )}
              </Grid>
            )}
          </Box>
        </Grid>
      )}

      {props?.type === "card" && (
        <Paper style={{ backgroundColor: "#F8F8FF" }}>
          <Card
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "8px",
              padding: "5px",
              width: "100%",
              boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
              border: "1px solid #ffffff"
            }}
          >
            <CardHeader
              title={
                <Typography
                  id={props?.id}
                  variant="h6"
                  style={{
                    fontWeight: "bold",
                    color: "#333",

                    fontSize: "1.25rem"
                  }}
                >
                  {props?.title}
                </Typography>
              }
              style={{ padding: "20px" }}
            />
          </Card>
          {props?.children}
        </Paper>
      )}
      {props?.type === "simple" && (
        <Grid
          item
          container
          spacing={2}
          sx={{ ...props?.headerSx, marginLeft: 0, width: "100%" }}
        >
          <Grid
            item
            sm={12}
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              "&.MuiGrid-item": { paddingTop: 0, height: 50 }
            }}
          >
            <Typography className="header-text">
              {hasBackButton && (
                <WSBackButton id="btnScheduleBack" url={newUrl} />
              )}
              {props?.title}
            </Typography>
          </Grid>
        </Grid>
      )}
      {props?.type === "light" && (
        <Grid
          sx={{
            backgroundColor: "white",
            borderRadius: 2,
            p: 2,
            boxShadow: 1,
            marginBottom: 0,
            width: "100%"
          }}
        >
          <Typography color={"#4AD16F"}>
            {hasBackButton && (
              <WSBackButton id="btnScheduleBack" url={newUrl} sx={{color:"#4AD16F"}}/>
            )}
            {props?.title}
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {props?.search && (
              <TextField
                size="small"
                placeholder="Search"
                sx={{
                  backgroundColor: "#c9c9c9",
                  borderRadius: 1,
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: "none"
                  }
                }}
              />
            )}

            {props?.action && (
              <Grid sx={{ display: "flex", gap: 2 }}>
                {props.action && (
                  <WSButton
                    key={props.action.text}
                    id={props.action.text}
                    label={props.action.text}
                    type={props.action?.type || "button"}
                    size={props.action?.size || "medium"}
                    sx={props.action?.sx}
                    onClick={props.action?.onClick}
                    className="ws-theme-crm"
                    color={props.action?.color}
                    variant={props.action?.variant || "contained"}
                    disabled={props.action.disabled}
                    loading={props.action?.loading}
                  />
                )}
              </Grid>
            )}
          </Box>
        </Grid>
      )}

      {props?.type === "new" && (
        <Grid
          sx={{
            background: "white",
            borderRadius: 3,
            px: 3,
            py: 2,
            boxShadow: "0 2px 12px rgba(0,0,0,0.05)",
            mb: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: "#022324",
              fontWeight: 600,
              fontSize: "1.25rem",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            {props?.icon}
            {props?.title}
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {props?.search && (
              <TextField
                size="small"
                placeholder="Search..."
                InputProps={{
                  sx: {
                    backgroundColor: "white",
                    borderRadius: 2,
                    px: 1,
                    boxShadow: "0px 1px 3px rgba(0,0,0,0.08)",
                  },
                  startAdornment: (
                    <Box component="span" sx={{ color: "#9e9e9e", mr: 1 }}>
                      🔍
                    </Box>
                  ),
                }}
              />
            )}

            {props?.action && (
              <WSButton
                key={props.action.text}
                id={props.action.text}
                label={props.action.text}
                type={props.action?.type || "button"}
                size={props.action?.size || "medium"}
                sx={{
                  borderRadius: 2,
                  fontWeight: 600,
                  textTransform: "none",
                  px: 3,
                  ...props.action?.sx,
                }}
                onClick={props.action?.onClick}
                className="ws-theme-crm"
                color={props.action?.color || "primary"}
                variant={props.action?.variant || "contained"}
                disabled={props.action?.disabled}
                loading={props.action?.loading}
              />
            )}
          </Box>
        </Grid>
      )}

      {!props?.isDivider && <br />}
      {props?.isDivider && (
        <Grid item sm={12} xs={12} style={{ marginTop: 10 }}>
          <Divider />
          <br />
        </Grid>
      )}
    </>
  );
});
